import React from "react";

/**
 * Two-column, self-looping product demo for the landing page.
 * NOTE: no external images/GIFs — the animation is pure CSS keyframes so it
 * stays crisp at any resolution, weighs almost nothing, and always mirrors
 * the actual UI palette. Loop cadence is ~14s so a visitor scrolling past
 * catches at least one full pass.
 *
 * Left demo:  PD Generator 3-step flow (title → questions → markdown)
 * Right demo: Blind-rating comparison overlay (radar with reveal)
 */
export default function LandingDemo() {
  return (
    <section className="mt-24" data-testid="landing-demo">
      <style>{DEMO_CSS}</style>
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-4">
        [03] See it in motion
      </div>
      <h2 className="font-display font-bold text-4xl tracking-tight mb-8">
        Clarity in Task. Clarity in Performance. Zero Bias.
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PdGeneratorDemo />
        <ComparisonOverlayDemo />
      </div>
    </section>
  );
}

/* ----------------------------- PD Generator demo ---------------------------
 * Three "screens" cross-fade in a 14s loop. Values on screen 1 are typed via
 * a step-function keyframe. Answers on screen 2 fade in one at a time.
 * Screen 3 reveals the markdown output line-by-line.
 * -------------------------------------------------------------------------- */
function PdGeneratorDemo() {
  return (
    <div className="cal-card cal-shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#111]/10">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          <span className="ml-3 font-mono-cal text-[10px] uppercase text-[#666]">calibrate.management / pd-generator</span>
        </div>
        <span className="font-mono-cal text-[10px] uppercase text-[#FF4F00]">AI DRAFT · ~30s</span>
      </div>

      <div className="relative h-[380px] bg-white" data-testid="demo-pd-generator">
        {/* Screen 1: Job intake */}
        <div className="pd-demo-screen pd-demo-screen-1 absolute inset-0 p-6">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-4">STEP 1 · TELL US THE ROLE</div>
          <label className="block font-mono-cal text-[10px] uppercase text-[#666] mb-1">JOB TITLE</label>
          <div className="border border-[#111] bg-[#F7F7F5] p-3 mb-4 font-medium">
            <span className="pd-typewriter-title" />
            <span className="pd-caret" />
          </div>
          <label className="block font-mono-cal text-[10px] uppercase text-[#666] mb-1">DEPARTMENT</label>
          <div className="border border-[#111] bg-[#F7F7F5] p-3 mb-6 font-medium">
            <span className="pd-typewriter-dept" />
            <span className="pd-caret" />
          </div>
          <div className="cal-btn inline-flex items-center gap-2 pd-btn-flash">
            <span>Generate discovery questions →</span>
          </div>
        </div>

        {/* Screen 2: Discovery questions with answers filling in */}
        <div className="pd-demo-screen pd-demo-screen-2 absolute inset-0 p-6">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-4">STEP 2 · ANSWER 4-5 QUESTIONS</div>
          {[
            "What are the top 3 outcomes this role must deliver?",
            "What team size and stakeholder mix?",
            "Which tools/tech are essential day-1?",
          ].map((q, i) => (
            <div key={i} className={`pd-q pd-q-${i} mb-3`}>
              <div className="text-xs text-[#666] mb-1">{q}</div>
              <div className="border border-[#111] bg-[#F7F7F5] px-3 py-2 text-sm">
                <span className={`pd-a pd-a-${i}`} />
              </div>
            </div>
          ))}
          <div className="cal-btn inline-flex items-center gap-2 mt-2 pd-btn-flash-2">
            <span>Generate PD →</span>
          </div>
        </div>

        {/* Screen 3: Markdown result stream */}
        <div className="pd-demo-screen pd-demo-screen-3 absolute inset-0 p-6 overflow-hidden">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C] mb-3">STEP 3 · POSITION DESCRIPTION READY</div>
          <div className="font-display font-bold text-lg mb-2 pd-md-line pd-md-line-0"># Position Description: Data Analyst</div>
          <div className="text-xs text-[#666] mb-3 pd-md-line pd-md-line-1">Growth Marketing · Full-time</div>
          <div className="text-xs font-mono-cal uppercase text-[#666] mb-1 pd-md-line pd-md-line-2">## Key Responsibilities</div>
          <ul className="text-sm space-y-1 pl-4 list-disc marker:text-[#FF4F00]">
            <li className="pd-md-line pd-md-line-3">Build SQL + Python dashboards for GTM leadership</li>
            <li className="pd-md-line pd-md-line-4">Own weekly cohort + funnel reports</li>
            <li className="pd-md-line pd-md-line-5">Partner with Growth on experiment design</li>
          </ul>
          <div className="text-xs font-mono-cal uppercase text-[#666] mt-3 mb-1 pd-md-line pd-md-line-6">## Key Performance Indicators</div>
          <div className="text-sm pd-md-line pd-md-line-7">3-5 measurable success metrics for year one…</div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-[#111]/10 flex items-center justify-between">
        <div className="font-mono-cal text-[10px] uppercase text-[#666]">PD GENERATOR · CLAUDE 3.5 SONNET</div>
        <div className="flex gap-1">
          <span className="w-1.5 h-1.5 bg-[#111] pd-dot pd-dot-1" />
          <span className="w-1.5 h-1.5 bg-[#111] pd-dot pd-dot-2" />
          <span className="w-1.5 h-1.5 bg-[#111] pd-dot pd-dot-3" />
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Comparison overlay demo ------------------------
 * 6-axis radar SVG. Two polygons animate in — the manager's outer polygon
 * (solid orange) and the employee's inner polygon (dashed). One axis label
 * highlights as "biggest gap" at the end of the loop.
 * -------------------------------------------------------------------------- */
function ComparisonOverlayDemo() {
  // Precomputed 6-axis polygon points on a normalized 100x100 canvas centred
  // at (50,50) with radius 40. Values are scores on a 0-5 scale.
  const axes = [
    "Delivers reports",
    "Owns dashboards",
    "Stakeholder mgmt",
    "Experiment design",
    "SQL fluency",
    "Coaching juniors",
  ];
  const managerScores = [5, 4, 3, 5, 5, 4];
  const employeeScores = [3, 4, 5, 2, 4, 3];

  const toPoints = (scores) => scores.map((s, i) => {
    const angle = (Math.PI * 2 * i) / axes.length - Math.PI / 2;
    const r = (s / 5) * 40;
    return `${50 + Math.cos(angle) * r},${50 + Math.sin(angle) * r}`;
  }).join(" ");

  const axisEndpoints = axes.map((_, i) => {
    const angle = (Math.PI * 2 * i) / axes.length - Math.PI / 2;
    return { x: 50 + Math.cos(angle) * 40, y: 50 + Math.sin(angle) * 40, i };
  });

  return (
    <div className="cal-card cal-shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#111]/10">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          <span className="ml-3 font-mono-cal text-[10px] uppercase text-[#666]">calibrate.management / comparison</span>
        </div>
        <span className="font-mono-cal text-[10px] uppercase text-[#00A36C] overlay-lock-label">BOTH SUBMITTED · UNLOCKED</span>
      </div>

      <div className="relative h-[380px] bg-white p-6 overflow-hidden" data-testid="demo-comparison-overlay">
        <div className="grid grid-cols-5 gap-4 h-full">
          <div className="col-span-3 relative overflow-visible min-w-0">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {/* Concentric grid rings */}
              {[0.2, 0.4, 0.6, 0.8, 1.0].map((r) => (
                <polygon key={r}
                  points={axes.map((_, i) => {
                    const a = (Math.PI * 2 * i) / axes.length - Math.PI / 2;
                    return `${50 + Math.cos(a) * 40 * r},${50 + Math.sin(a) * 40 * r}`;
                  }).join(" ")}
                  fill="none" stroke="#E5E5E0" strokeWidth="0.3" />
              ))}
              {/* Spokes */}
              {axisEndpoints.map(({ x, y, i }) => (
                <line key={i} x1="50" y1="50" x2={x} y2={y} stroke="#E5E5E0" strokeWidth="0.3" />
              ))}
              {/* Manager polygon — solid, filled, drawn first */}
              <polygon
                className="overlay-poly overlay-poly-manager"
                points={toPoints(managerScores)}
                fill="#FF4F00" fillOpacity="0.18"
                stroke="#FF4F00" strokeWidth="0.9"
              />
              {/* Employee polygon — dashed */}
              <polygon
                className="overlay-poly overlay-poly-employee"
                points={toPoints(employeeScores)}
                fill="none"
                stroke="#111" strokeWidth="0.9" strokeDasharray="1.5,1"
              />
              {/* "Biggest gap" callout dot on axis index 3 (Experiment design) */}
              <circle
                className="overlay-gap-dot"
                cx={50 + Math.cos((Math.PI * 2 * 3) / 6 - Math.PI / 2) * (2 / 5 * 40)}
                cy={50 + Math.sin((Math.PI * 2 * 3) / 6 - Math.PI / 2) * (2 / 5 * 40)}
                r="1.6" fill="#FF4F00"
              />
            </svg>
            {/* Axis labels around the radar. Positioned absolutely so they
                stay in flow when the SVG scales responsively. */}
            {axes.map((label, i) => {
              const angle = (Math.PI * 2 * i) / axes.length - Math.PI / 2;
              const x = 50 + Math.cos(angle) * 48;
              const y = 50 + Math.sin(angle) * 48;
              return (
                <div
                  key={i}
                  className={`absolute font-mono-cal text-[9px] uppercase tracking-wide overlay-label overlay-label-${i}`}
                  style={{
                    left: `${x}%`, top: `${y}%`,
                    transform: "translate(-50%, -50%)",
                    color: i === 3 ? "#FF4F00" : "#666",
                    fontWeight: i === 3 ? 700 : 400,
                  }}
                >
                  {label}
                </div>
              );
            })}
          </div>

          <div className="col-span-2 flex flex-col justify-center">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">LEGEND</div>
            <div className="flex items-center gap-2 mb-1 text-xs">
              <span className="inline-block w-4 h-0.5 bg-[#FF4F00]" />
              <span className="font-medium">Manager rating</span>
            </div>
            <div className="flex items-center gap-2 mb-4 text-xs">
              <span className="inline-block w-4 h-0.5 border-b border-dashed border-[#111]" style={{borderBottomWidth:"1px"}} />
              <span className="font-medium">Employee rating</span>
            </div>
            <div className="cal-card p-3 overlay-gap-card">
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-1">BIGGEST GAP</div>
              <div className="text-sm font-medium">Experiment design</div>
              <div className="text-xs text-[#666] mt-1">Manager: 5 · Employee: 2</div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-[#111]/10 flex items-center justify-between">
        <div className="font-mono-cal text-[10px] uppercase text-[#666]">COMPARISON OVERLAY · UNLOCKED</div>
        <span className="font-mono-cal text-[10px] uppercase text-[#00A36C]">✓ BOTH SUBMITTED</span>
      </div>
    </div>
  );
}

/* ------------------------------- animations -------------------------------
 * All keyframes and per-element delays live here so the JSX stays readable.
 * Timeline uses a 14s loop:
 *   0.0s-4.5s   : PD screen 1 (typing)
 *   4.5s-9.0s   : PD screen 2 (questions + answers)
 *   9.0s-14.0s  : PD screen 3 (markdown reveal)
 *   Overlay demo runs its own 14s cycle in parallel.
 * -------------------------------------------------------------------------- */
const DEMO_CSS = `
/* Cross-fade between the three PD Generator screens */
.pd-demo-screen {
  opacity: 0;
  transition: opacity 0.5s ease;
}
.pd-demo-screen-1 { animation: pd-show-1 14s infinite; }
.pd-demo-screen-2 { animation: pd-show-2 14s infinite; }
.pd-demo-screen-3 { animation: pd-show-3 14s infinite; }
@keyframes pd-show-1 { 0%,30% {opacity:1;} 32%,100% {opacity:0;} }
@keyframes pd-show-2 { 0%,32% {opacity:0;} 34%,60% {opacity:1;} 62%,100% {opacity:0;} }
@keyframes pd-show-3 { 0%,62% {opacity:0;} 64%,100% {opacity:1;} }

/* Typewriter effect using ::after and step keyframes — no JS needed */
.pd-typewriter-title::after {
  content: "Data Analyst";
  color: #111;
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  max-width: 0;
  vertical-align: bottom;
  animation: pd-type-title 14s steps(11) infinite;
}
.pd-typewriter-dept::after {
  content: "Growth Marketing";
  color: #111;
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  max-width: 0;
  vertical-align: bottom;
  animation: pd-type-dept 14s steps(16) infinite;
  animation-delay: 0s;
}
@keyframes pd-type-title { 0% {max-width:0;} 12%,100% {max-width:11ch;} }
@keyframes pd-type-dept { 0%,12% {max-width:0;} 24%,100% {max-width:16ch;} }
.pd-caret {
  display: inline-block; width: 1px; height: 1em; background: #111; margin-left: 2px;
  vertical-align: -2px; animation: caret-blink 1s steps(2) infinite;
}
@keyframes caret-blink { 0%,50% {opacity:1;} 51%,100% {opacity:0;} }

/* Button pulse just as the flow advances */
.pd-btn-flash { animation: pd-btn-flash 14s infinite; }
.pd-btn-flash-2 { animation: pd-btn-flash-2 14s infinite; }
@keyframes pd-btn-flash { 0%,28% {transform:scale(1);} 30% {transform:scale(1.04); box-shadow: 0 0 0 4px rgba(255,79,0,0.15);} 32%,100% {transform:scale(1); box-shadow: none;} }
@keyframes pd-btn-flash-2 { 0%,58% {transform:scale(1);} 60% {transform:scale(1.04); box-shadow: 0 0 0 4px rgba(255,79,0,0.15);} 62%,100% {transform:scale(1); box-shadow: none;} }

/* Discovery answers fade in staggered during screen 2's window */
.pd-a-0::after { content: "SQL dashboards, cohort reports, experiment design"; color:#111; display:inline-block; opacity:0; animation: pd-a-fade 14s infinite; }
.pd-a-1::after { content: "Team of 3, reports to VP Growth"; color:#111; display:inline-block; opacity:0; animation: pd-a-fade 14s infinite; animation-delay: 1s; }
.pd-a-2::after { content: "SQL, Python, Looker, dbt"; color:#111; display:inline-block; opacity:0; animation: pd-a-fade 14s infinite; animation-delay: 2s; }
@keyframes pd-a-fade { 0%,34% {opacity:0;} 40%,60% {opacity:1;} 62%,100% {opacity:0;} }

/* Markdown lines reveal one after another during screen 3 */
.pd-md-line { opacity: 0; transform: translateY(4px); animation: pd-md 14s infinite; }
.pd-md-line-0 { animation-delay: 0s; }
.pd-md-line-1 { animation-delay: 0.15s; }
.pd-md-line-2 { animation-delay: 0.3s; }
.pd-md-line-3 { animation-delay: 0.45s; }
.pd-md-line-4 { animation-delay: 0.6s; }
.pd-md-line-5 { animation-delay: 0.75s; }
.pd-md-line-6 { animation-delay: 0.95s; }
.pd-md-line-7 { animation-delay: 1.1s; }
@keyframes pd-md {
  0%,64% {opacity:0; transform:translateY(4px);}
  70%,96% {opacity:1; transform:translateY(0);}
  100% {opacity:0;}
}

/* Working-dots in the PD demo footer */
.pd-dot { animation: pd-dot 1.2s ease-in-out infinite; }
.pd-dot-1 { animation-delay: 0s; }
.pd-dot-2 { animation-delay: 0.15s; }
.pd-dot-3 { animation-delay: 0.3s; }
@keyframes pd-dot { 0%,80%,100% {opacity:0.15;} 40% {opacity:1;} }

/* --------------------------- Overlay demo animations --------------------- */
.overlay-poly {
  stroke-dasharray: 300;
  stroke-dashoffset: 300;
  fill-opacity: 0;
  animation: overlay-draw 14s ease-out infinite;
}
.overlay-poly-manager { animation-delay: 0.5s; }
.overlay-poly-employee { animation-delay: 2s; }
@keyframes overlay-draw {
  0% {stroke-dashoffset:300; fill-opacity:0;}
  20% {stroke-dashoffset:0; fill-opacity:0.18;}
  90% {stroke-dashoffset:0; fill-opacity:0.18;}
  100% {stroke-dashoffset:0; fill-opacity:0;}
}
.overlay-poly-employee { animation-name: overlay-draw-line; }
@keyframes overlay-draw-line {
  0% {stroke-dashoffset:300;}
  25% {stroke-dashoffset:0;}
  90% {stroke-dashoffset:0;}
  100% {stroke-dashoffset:0;}
}

.overlay-gap-dot { opacity: 0; animation: overlay-gap-dot 14s infinite; }
@keyframes overlay-gap-dot { 0%,40% {opacity:0; r:1.6;} 50% {opacity:1; r:3;} 55%,90% {opacity:1; r:1.8;} 100% {opacity:0;} }

.overlay-gap-card { opacity: 0; transform: translateY(6px); animation: overlay-gap-card 14s infinite; }
@keyframes overlay-gap-card { 0%,45% {opacity:0; transform:translateY(6px);} 55%,90% {opacity:1; transform:translateY(0);} 100% {opacity:0;} }

/* Axis labels fade in with the manager polygon */
.overlay-label { opacity: 0; animation: overlay-label 14s infinite; }
.overlay-label-0 { animation-delay: 0.1s; }
.overlay-label-1 { animation-delay: 0.2s; }
.overlay-label-2 { animation-delay: 0.3s; }
.overlay-label-3 { animation-delay: 0.4s; }
.overlay-label-4 { animation-delay: 0.5s; }
.overlay-label-5 { animation-delay: 0.6s; }
@keyframes overlay-label { 0%,5% {opacity:0;} 15%,90% {opacity:1;} 100% {opacity:0;} }

.overlay-lock-label { animation: overlay-lock 14s infinite; }
@keyframes overlay-lock {
  0%,15% {color:#666; content:"LOCKED · WAITING…";}
  20%,100% {color:#00A36C;}
}

/* Respect users who explicitly disable motion. Show the final frame instead
   of hiding the demo entirely so the layout doesn't jump. */
@media (prefers-reduced-motion: reduce) {
  .pd-demo-screen-3 { opacity: 1 !important; animation: none !important; }
  .pd-demo-screen-1, .pd-demo-screen-2 { display: none !important; }
  .pd-md-line { opacity: 1 !important; transform: none !important; animation: none !important; }
  .overlay-poly, .overlay-poly-manager, .overlay-poly-employee { animation: none !important; stroke-dashoffset: 0 !important; fill-opacity: 0.18 !important; }
  .overlay-poly-employee { fill-opacity: 0 !important; }
  .overlay-label, .overlay-gap-card, .overlay-gap-dot { animation: none !important; opacity: 1 !important; transform: none !important; }
  .pd-typewriter-title::after, .pd-typewriter-dept::after { animation: none !important; max-width: none !important; }
  .pd-caret { display: none !important; }
  .pd-dot { animation: none !important; opacity: 0.6 !important; }
}
`;
